'use client';

import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '~/lib/utils';

export type TableSortOrder = 'ASC' | 'DESC' | null | undefined;

export function columnSortOrder(
  activeKey: string | undefined,
  columnKey: string,
  order: 'ASC' | 'DESC' | undefined,
): TableSortOrder {
  return activeKey === columnKey ? (order ?? null) : null;
}

export function tableAriaSort(order: TableSortOrder): 'ascending' | 'descending' | 'none' {
  if (order === 'ASC') return 'ascending';
  if (order === 'DESC') return 'descending';
  return 'none';
}

export function TableSortIcon({
  order,
  className,
}: {
  order: TableSortOrder;
  className?: string;
}) {
  const iconClass = cn('size-3.5 shrink-0', className);
  if (order === 'ASC') return <ChevronUp className={iconClass} strokeWidth={2} />;
  if (order === 'DESC') return <ChevronDown className={iconClass} strokeWidth={2} />;
  return <ChevronsUpDown className={cn(iconClass, 'opacity-50')} strokeWidth={2} />;
}

function sortAriaLabel(label: string, order: TableSortOrder) {
  if (order === 'ASC') return `Sort by ${label}, currently ascending`;
  if (order === 'DESC') return `Sort by ${label}, currently descending`;
  return `Sort by ${label}`;
}

type TableSortControlProps = {
  label: ReactNode;
  sortLabel?: string;
  order: TableSortOrder;
  onClick: () => void;
  className?: string;
};

export function TableSortControl({
  label,
  sortLabel,
  order,
  onClick,
  className,
}: TableSortControlProps) {
  const name = sortLabel ?? (typeof label === 'string' ? label : 'column');
  const isActive = order === 'ASC' || order === 'DESC';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={sortAriaLabel(name, order)}
      className={cn(
        'focus-visible:ring-ring/50 inline-flex items-center gap-1 rounded-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
        isActive ? 'text-foreground' : 'hover:text-foreground',
        className,
      )}
    >
      {label}
      <TableSortIcon order={order} />
    </button>
  );
}

type SortableTableHeaderProps = TableSortControlProps & {
  align?: 'left' | 'right' | 'center';
  thClassName?: string;
};

export function SortableTableHeader({
  align = 'left',
  thClassName,
  className,
  ...controlProps
}: SortableTableHeaderProps) {
  return (
    <th
      className={cn(
        'px-2 pt-1 pb-3 font-medium',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        thClassName,
      )}
      aria-sort={tableAriaSort(controlProps.order)}
    >
      <TableSortControl
        {...controlProps}
        className={cn(align === 'right' && 'ml-auto', align === 'center' && 'mx-auto', className)}
      />
    </th>
  );
}
